import React, { ReactNode, useEffect } from 'react';
import { connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';

import { changeLBSBank, fetchUserSession, showLBSBankSelect } from '../actions';
import { DispatchWithState, StringKeyValue } from '../actions/types';
import Loading from '../components/Messages/Loading';
import CustomAppTypes from '../config/customAppTypes';
import { AppState } from '../reducers';
import { ContextReducer } from '../reducers/context';
import { LbsReducer } from '../reducers/lbs';
import { LoaderReducer } from '../reducers/loader';
import parseUrlParams from '../util/urlParams';

interface Props {
    action: {
        fetchUserSession: (lbsBnr?: string) => DispatchWithState;
        changeLBSBank: (bankregnr: string) => void;
        showLBSBankSelect: (show: boolean) => void;
    };
    loader: LoaderReducer;
    text: StringKeyValue;
    lbs: LbsReducer;
    context: ContextReducer;
    children: ReactNode;
}

const GlobalLoader = (props: Props) => {
    useEffect(() => {
        const { action, context, lbs } = props;
        const urlParams = parseUrlParams();
        const lbsParamBnr = urlParams.lbsbnr && lbs.lbsBanks.find((b) => b.value === urlParams.lbsbnr);

        if (lbsParamBnr && lbs.lbsBankregnr !== lbsParamBnr.value) {
            action.changeLBSBank(lbsParamBnr.value);
        } else if (CustomAppTypes.isLbs(context) && lbs.lbsBankregnr === null && !context.showLBSBankSelect) {
            action.showLBSBankSelect(true);
        } else {
            action.fetchUserSession();
        }
    }, [props]);

    const { text, loader, children } = props;
    const loaders = [
        {
            isLoading: loader.isFetchingUserSession,
            title: text.loadingDefault,
        },
        {
            isLoading: loader.isFetchingUserContext,
            title: text.loadingDefault,
        },
        {
            isLoading: loader.isFetchingNafCache,
            title: text.loadingDefault,
        },
        {
            isLoading: loader.isFetchingCards,
            title: text.loadingDefault,
        },
        {
            isLoading: loader.isFetchingStatus,
            title: text.loadingDefault,
        },
        {
            isLoading: loader.isFetchingCase,
            title: text.loadingCase,
        },
        {
            isLoading: loader.isCreatingSblConsent,
            title: text.loadingDefault,
        },
        {
            isLoading: loader.isFetchingAgriMember,
            title: text.loadingDefault,
        },
        {
            isLoading: loader.isFetchingSigningUrl,
            title: text.loadingDefault,
        },
        {
            isLoading: loader.isCompletingOrder,
            title: text.loadingDefault,
        },
    ];

    for (const i in loaders) {
        if (loaders[i].isLoading) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return (
                <div>
                    <Loading title={loaders[i].title || text.loadingDefault} />
                </div>
            );
        }
    }

    return children;
};

const mapStateToProps = (state: AppState) => ({
    text: state.context.config.data.text,
    loader: state.loader,
    context: state.context,
    lbs: state.lbs,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    action: bindActionCreators(
        {
            fetchUserSession,
            changeLBSBank,
            showLBSBankSelect,
        },
        dispatch,
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(GlobalLoader);
